import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { apiClient } from "../services/Services";
import { Video } from "../types/Video";
import { ErrorState } from "./errors";

interface ClassConfig {
    shape: string;
    color: string;
    lineWidth: number;
    radius?: number;
}

interface RequestBody {
    classConfigs: Record<string, ClassConfig>;
    defaultConfig: ClassConfig;
    skipUnconfigured: boolean;
}

interface DownloadAnnotatedVideoFileParams {
    deviceId: string;
    timestamp: string;
    filename: string;
}

interface MutationVariables extends DownloadAnnotatedVideoFileParams {
    requestBody: RequestBody;
}

interface AnnotatedVideoDownloadOptions
    extends Omit<
        UseMutationOptions<void, Error, MutationVariables, unknown>,
        "mutationFn"
    > {
    setErrorState: (state: ErrorState) => void;
}

export const fetchVideo = async (
    deviceId: string,
    timestamp: string,
): Promise<any> => {
    try {
        const response: any = await apiClient.get(
            `devices/${deviceId}/videos?time=${timestamp}`,
        );
        return response.data;
    } catch (error) {
        console.log(error);
        throw error;
    }
};

export default function useAnnotatedDownloadFile(
    options: AnnotatedVideoDownloadOptions,
) {
    const mutation = useMutation({
        mutationFn: async ({
            deviceId,
            timestamp,
            requestBody,
        }: MutationVariables) => {
            const videos: Video[] = await fetchVideo(deviceId, timestamp);
            const videoId: string = videos[0]?.id;

            if (!videoId) {
                options.setErrorState({
                    hasError: true,
                    message: "No video found for the given timestamp",
                });
            }

            const response = await apiClient.post(
                `videos/${videoId}`,
                requestBody,
                {
                    responseType: "blob",
                    headers: {
                        accept: "video/mp4",
                    },
                },
            );
            return response;
        },
        onSuccess: (response, variables) => {
            if (!(response instanceof Blob)) {
                throw new Error("Faild to download: Invalid response format");
            }
            const downloadUrl = window.URL.createObjectURL(response);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = variables.filename;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        },
        onError: (error) => {
            console.error("Download failed", error);
            options.setErrorState({
                hasError: true,
                message: error.message,
            });
        },
    });

    return mutation;
}
